import { Close as CloseIcon } from '@tunasong/icons'
import type { Owned } from '@tunasong/models'
import type { ACL } from '@tunasong/schemas'
import type { FC } from 'react'
import { UserAvatar } from '../profile/index.js'
import { visibleAcls } from './acl-visibility.js'
import { memberLabel, useMemberEmails } from './member-emails.hook.js'
import { permissionLabel } from './permission-label.js'
import { graphHooks } from '@tunasong/graph-lib/react'
import { IconButton, ListItem, ListItemIcon, List, ListItemText } from '../primitives/index.js'

export interface ACLListProps {
  entity?: Owned
  /** Principal list that is pending a server operation */
  pendingPrincipals?: string[]
  onDelete?(acl: ACL): void
}

/** Show the ACL list for an entity */
export const ACLList: FC<ACLListProps> = props => {
  const { entity, pendingPrincipals = [], onDelete } = props
  const { acls: providedAcls = [], userId } = entity ?? {}
  /** Deduped, and without `link:` credentials — see `visibleAcls`. */
  const acls = visibleAcls(providedAcls)
  const userIds = acls.map(acl => acl.principal)
  const { profiles } = graphHooks.useProfiles({ userIds, includeAssistant: false })
  const emails = useMemberEmails(entity)
  const handleDelete = (a: ACL) => () => (onDelete ? onDelete(a) : null)

  if (acls.length === 0 && pendingPrincipals.length === 0) {
    return null
  }

  return (
    <List>
      {acls.some(a => a.principal === 'PUBLIC') ? (
        <ListItem>
          <ListItemIcon>
            <UserAvatar aria-label="user" userId="PUBLIC" />
          </ListItemIcon>
          <ListItemText primary="Public" secondary="Anyone can view" />
        </ListItem>
      ) : null}
      {acls
        .filter(acl => acl.principal && acl.principal !== userId && acl.principal !== 'PUBLIC') // Don't show owner in shared list
        .map((a, idx) => {
          const profile = profiles?.find(p => p.userId === a.principal)
          const label = memberLabel(profile, emails) ?? `Loading ${a.principal}...`
          const email = emails[a.principal]
          const permission = permissionLabel(a.permission)
          const secondary = email && email !== label ? `${email} · ${permission}` : permission
          return (
            <ListItem
              key={idx}
              /** As the item's secondary action, the remove button gets its 48px reserved: text never runs under it */
              secondaryAction={
                onDelete ? (
                  <IconButton
                    aria-label={`Remove ${memberLabel(profile, emails) ?? a.principal}`}
                    onClick={handleDelete(a)}
                  >
                    <CloseIcon />
                  </IconButton>
                ) : undefined
              }
            >
              <ListItemIcon>
                <UserAvatar aria-label="user" userId={profile?.userId} />
              </ListItemIcon>
              {/* A long e-mail breaks inside itself rather than pushing past the row */}
              <ListItemText primary={label} secondary={secondary} style={{ minWidth: 0, overflowWrap: 'anywhere' }} />
            </ListItem>
          )
        })}
      {pendingPrincipals.map((a, idx) => (
        <ListItem key={idx}>
          <ListItemIcon>
            <UserAvatar aria-label="user" fallbackName={a} />
          </ListItemIcon>
          <ListItemText primary={a} secondary="Pending..." />
        </ListItem>
      ))}
    </List>
  )
}

export default ACLList
